import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = {
  class: "bottom-bar",
  "data-theme-dark": ""
}
const _hoisted_2 = {
  key: 0,
  class: "bottom-bar-call-controls"
}
const _hoisted_3 = { class: "bottom-bar-call-controls" }

import {
	showError,
	showWarning,
} from '@nextcloud/dialogs'
import { t } from '@nextcloud/l10n'
import { useHotKey } from '@nextcloud/vue/composables/useHotKey'
import { computed, ref, toValue, watch } from 'vue'
import { useStore } from 'vuex'
import NcButton from '@nextcloud/vue/components/NcButton'
import NcLoadingIcon from '@nextcloud/vue/components/NcLoadingIcon'
import IconFullscreen from 'vue-material-design-icons/Fullscreen.vue'
import IconFullscreenExit from 'vue-material-design-icons/FullscreenExit.vue'
import IconHandBackLeft from 'vue-material-design-icons/HandBackLeft.vue' // Filled for better indication
import IconHandBackLeftOutline from 'vue-material-design-icons/HandBackLeftOutline.vue'
import IconSubtitles from 'vue-material-design-icons/Subtitles.vue'
import IconSubtitlesOutline from 'vue-material-design-icons/SubtitlesOutline.vue'
import IconViewGalleryOutline from 'vue-material-design-icons/ViewGalleryOutline.vue'
import IconViewGridOutline from 'vue-material-design-icons/ViewGridOutline.vue'
import CallButton from '../TopBar/CallButton.vue'
import ReactionMenu from '../TopBar/ReactionMenu.vue'
import TopBarMediaControls from '../TopBar/TopBarMediaControls.vue'
import {
	toggleFullscreen,
	useDocumentFullscreen,
} from '../../composables/useDocumentFullscreen.ts'
import { useGetToken } from '../../composables/useGetToken.ts'
import { CONVERSATION, PARTICIPANT } from '../../constants.ts'
import { getTalkConfig } from '../../services/CapabilitiesManager.ts'
import { useActorStore } from '../../stores/actor.ts'
import { useBreakoutRoomsStore } from '../../stores/breakoutRooms.ts'
import { useCallViewStore } from '../../stores/callView.ts'
import { useLiveTranscriptionStore } from '../../stores/liveTranscription.ts'
import { localCallParticipantModel, localMediaModel } from '../../utils/webrtc/index.js'

const AUTO_LOWER_HAND_THRESHOLD = 3000

export default /*@__PURE__*/_defineComponent({
  __name: 'BottomBar',
  props: {
    isSidebar: { type: Boolean, default: false }
  },
  setup(__props: any) {


const disableKeyboardShortcuts = OCP.Accessibility.disableKeyboardShortcuts()

const store = useStore()
const token = useGetToken()
const actorStore = useActorStore()
const breakoutRoomsStore = useBreakoutRoomsStore()
const isFullscreen = !__props.isSidebar && useDocumentFullscreen()
const callViewStore = useCallViewStore()
const liveTranscriptionStore = useLiveTranscriptionStore()

const isLiveTranscriptionLoading = ref(false)

const conversation = computed(() => {
	return store.getters.conversation(token.value) || store.getters.dummyConversation
})

const supportedReactions = computed(() => getTalkConfig(token.value, 'call', 'supported-reactions') || [])

const hasReactionSupport = computed(() => supportedReactions.value && supportedReactions.value.length > 0)

const canModerate = computed(() => [PARTICIPANT.TYPE.OWNER, PARTICIPANT.TYPE.MODERATOR, PARTICIPANT.TYPE.GUEST_MODERATOR]
	.includes(conversation.value.participantType))

const isLiveTranscriptionSupported = computed(() => getTalkConfig(token.value, 'call', 'live-transcription') || false)

const liveTranscriptionButtonLabel = computed(() => {
	if (!callViewStore.isLiveTranscriptionEnabled) {
		return t('spreed', 'Enable live transcription')
	}

	return t('spreed', 'Disable live transcription')
})

const isHandRaised = computed(() => localMediaModel.attributes.raisedHand.state === true)

const raiseHandButtonLabel = computed(() => {
	if (!isHandRaised.value) {
		return disableKeyboardShortcuts
			? t('spreed', 'Raise hand')
			: t('spreed', 'Raise hand (R)')
	}
	return disableKeyboardShortcuts
		? t('spreed', 'Lower hand')
		: t('spreed', 'Lower hand (R)')
})

const fullscreenLabel = computed(() => {
	return toValue(isFullscreen)
		? t('spreed', 'Exit full screen (F)')
		: t('spreed', 'Full screen (F)')
})

const changeViewLabel = computed(() => {
	return isGrid.value
		? t('spreed', 'Speaker view')
		: t('spreed', 'Grid view')
})

const showCallLayoutSwitch = computed(() => !callViewStore.isEmptyCallView)
const isGrid = computed(() => callViewStore.isGrid)
const userIsInBreakoutRoomAndInCall = computed(() => conversation.value.objectType === CONVERSATION.OBJECT_TYPE.BREAKOUT_ROOM)

/**
 * Toggle live transcriptions.
 */
async function toggleLiveTranscription() {
	if (isLiveTranscriptionLoading.value) {
		return
	}

	isLiveTranscriptionLoading.value = true

	if (!callViewStore.isLiveTranscriptionEnabled) {
		await enableLiveTranscription()
	} else {
		await disableLiveTranscription()
	}

	isLiveTranscriptionLoading.value = false
}

/**
 * Enable live transcriptions.
 */
async function enableLiveTranscription() {
	// Strictly speaking it would be the responsibility of the components using
	// the language metadata to ensure that it is loaded before using it, but
	// for simplicity it is done here and enabling the live transcription is
	// tied to having said metadata.
	try {
		await liveTranscriptionStore.loadLiveTranscriptionLanguages()
	} catch (exception) {
		showError(t('spreed', 'Error when trying to load the available live transcription languages'))

		return
	}

	try {
		await callViewStore.enableLiveTranscription(token.value)
	} catch (error) {
		showError(t('spreed', 'Failed to enable live transcription'))
	}
}

/**
 * Disable live transcriptions.
 */
async function disableLiveTranscription() {
	try {
		await callViewStore.disableLiveTranscription(token.value)
	} catch (error) {
		// Not being able to disable the live transcription is not really
		// relevant for the user, as the transcript will be no longer visible in
		// the UI anyway, so no error is shown in that case.
	}
}

let lowerHandDelay = AUTO_LOWER_HAND_THRESHOLD
let speakingTimestamp: number | null = null
let lowerHandTimeout: ReturnType<typeof setTimeout> | null = null

// Hand raising functionality
/**
 * Toggle the hand raised state for the local media model and update the store.
 * If the user is in a breakout room, it also handles the request for assistance.
 */
function toggleHandRaised() {
	const newState = !isHandRaised.value
	localMediaModel.toggleHandRaised(newState)
	store.dispatch('setParticipantHandRaised', {
		sessionId: actorStore.sessionId,
		raisedHand: localMediaModel.attributes.raisedHand,
	})

	// Handle breakout room assistance requests
	if (userIsInBreakoutRoomAndInCall.value && !canModerate.value) {
		const hasRaisedHands = Object.keys(store.getters.participantRaisedHandList)
			.filter((sessionId) => sessionId !== actorStore.sessionId)
			.length !== 0

		if (hasRaisedHands) {
			return // Assistance is already requested by someone in the room
		}

		const hasAssistanceRequested = conversation.value.breakoutRoomStatus === CONVERSATION.BREAKOUT_ROOM_STATUS.STATUS_ASSISTANCE_REQUESTED
		if (newState && !hasAssistanceRequested) {
			breakoutRoomsStore.requestAssistance(token.value)
		} else if (!newState && hasAssistanceRequested) {
			breakoutRoomsStore.dismissRequestAssistance(token.value)
		}
	}
}

// Auto-lower hand when speaking
watch(() => localMediaModel.attributes.speaking, (speaking) => {
	if (lowerHandTimeout !== null && !speaking) {
		lowerHandDelay = Math.max(0, lowerHandDelay - (Date.now() - speakingTimestamp!))
		clearTimeout(lowerHandTimeout)
		lowerHandTimeout = null
		return
	}

	// User is not speaking OR timeout is already running OR hand is not raised
	if (!speaking || lowerHandTimeout !== null || !isHandRaised.value) {
		return
	}

	speakingTimestamp = Date.now()
	lowerHandTimeout = setTimeout(() => {
		lowerHandTimeout = null
		speakingTimestamp = null
		lowerHandDelay = AUTO_LOWER_HAND_THRESHOLD

		if (isHandRaised.value) {
			toggleHandRaised()
		}
	}, lowerHandDelay)
})

/**
 * Switches the call view mode between grid and speaker view.
 */
function changeView() {
	callViewStore.setCallViewMode({ token: token.value, isGrid: !isGrid.value, clearLast: false })
	callViewStore.setSelectedVideoPeerId(null)
}

// Keyboard shortcuts
useHotKey('r', toggleHandRaised)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!__props.isSidebar)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_unref(NcButton), {
            "aria-label": fullscreenLabel.value,
            variant: _unref(isFullscreen) ? 'secondary' : 'tertiary',
            title: fullscreenLabel.value,
            onClick: _unref(toggleFullscreen)
          }, {
            icon: _withCtx(() => [
              (!_unref(isFullscreen))
                ? (_openBlock(), _createBlock(IconFullscreen, {
                    key: 0,
                    size: 20
                  }))
                : (_openBlock(), _createBlock(IconFullscreenExit, {
                    key: 1,
                    size: 20
                  }))
            ]),
            _: 1
          }, 8, ["aria-label", "variant", "title", "onClick"]),
          (showCallLayoutSwitch.value)
            ? (_openBlock(), _createBlock(_unref(NcButton), {
                key: 0,
                variant: "tertiary",
                "aria-label": changeViewLabel.value,
                title: changeViewLabel.value,
                onClick: changeView
              }, {
                icon: _withCtx(() => [
                  (!isGrid.value)
                    ? (_openBlock(), _createBlock(IconViewGridOutline, {
                        key: 0,
                        size: 20
                      }))
                    : (_openBlock(), _createBlock(IconViewGalleryOutline, {
                        key: 1,
                        size: 20
                      }))
                ]),
                _: 1
              }, 8, ["aria-label", "title"]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(TopBarMediaControls, {
        token: _unref(token),
        model: _unref(localMediaModel),
        "is-sidebar": __props.isSidebar,
        "local-call-participant-model": _unref(localCallParticipantModel)
      }, null, 8, ["token", "model", "is-sidebar", "local-call-participant-model"]),
      (hasReactionSupport.value)
        ? (_openBlock(), _createBlock(ReactionMenu, {
            key: 0,
            token: _unref(token),
            "supported-reactions": supportedReactions.value,
            "local-call-participant-model": _unref(localCallParticipantModel)
          }, null, 8, ["token", "supported-reactions", "local-call-participant-model"]))
        : _createCommentVNode("", true),
      (isLiveTranscriptionSupported.value)
        ? (_openBlock(), _createBlock(_unref(NcButton), {
            key: 1,
            title: liveTranscriptionButtonLabel.value,
            "aria-label": liveTranscriptionButtonLabel.value,
            variant: _unref(callViewStore).isLiveTranscriptionEnabled ? 'secondary' : 'tertiary',
            disabled: isLiveTranscriptionLoading.value,
            onClick: toggleLiveTranscription
          }, {
            icon: _withCtx(() => [
              (isLiveTranscriptionLoading.value)
                ? (_openBlock(), _createBlock(_unref(NcLoadingIcon), {
                    key: 0,
                    size: 20
                  }))
                : (_unref(callViewStore).isLiveTranscriptionEnabled)
                  ? (_openBlock(), _createBlock(IconSubtitles, {
                      key: 1,
                      size: 20
                    }))
                  : (_openBlock(), _createBlock(IconSubtitlesOutline, {
                      key: 2,
                      size: 20
                    }))
            ]),
            _: 1
          }, 8, ["title", "aria-label", "variant", "disabled"]))
        : _createCommentVNode("", true),
      (!__props.isSidebar)
        ? (_openBlock(), _createBlock(_unref(NcButton), {
            key: 2,
            title: raiseHandButtonLabel.value,
            "aria-label": raiseHandButtonLabel.value,
            variant: isHandRaised.value ? 'secondary' : 'tertiary',
            onClick: toggleHandRaised
          }, {
            icon: _withCtx(() => [
              (isHandRaised.value)
                ? (_openBlock(), _createBlock(IconHandBackLeft, {
                    key: 0,
                    size: 18
                  }))
                : (_openBlock(), _createBlock(IconHandBackLeftOutline, {
                    key: 1,
                    size: 18
                  }))
            ]),
            _: 1
          }, 8, ["title", "aria-label", "variant"]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(CallButton, {
      "hide-text": __props.isSidebar,
      "is-screensharing": !!_unref(localMediaModel).attributes.localScreen
    }, null, 8, ["hide-text", "is-screensharing"])
  ]))
}
}

})